/* global runtime, initFirebase, authorizedPost, createId, t */
// Avstemmingsmappa: dokumentasjon, kundeforespørsler og kontrollene som gjelder
// en periode. Lastes som klassisk script før backend.jsx og deler dens
// bindingsmiljø, som resten av domenefilene.
//
// Filen gaar rett til Storage — reglene der gir foretakets medlemmer
// skrivetilgang allerede. Backend faar bare stien, og avviser den hvis den peker
// utenfor periodens egen mappe.

// Kallet identifiserer bare foretak og periode. Functions leser regnskapsstate,
// MVA-regime og bankoppsett selv; klienten faar ikke sende kontrollgrunnlaget.
const buildReconciliationPeriodPayload = ({ businessId, periodId } = {}) => ({ businessId, periodId });

// Filen gaar rett til Storage, som resten av vedleggene i appen — reglene der
// gir foretakets medlemmer skrivetilgang allerede. Backend far bare stien, og
// avviser den hvis den peker utenfor denne periodens mappe.
const uploadReconciliationDocument = async ({ businessId = runtime.activeBusinessId, periodId, file, kind, areaKey, accountIds, requestId } = {}) => {
  await initFirebase();
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!businessId || !periodId) throw new Error(t('r1e.recon.businessOrPeriodMissing'));
  if (!file) throw new Error(t('svc.missingAttachment'));
  const id = createId('reconciliation-document');
  const storagePath = `businesses/${businessId}/reconciliations/${periodId}/${id}`;
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(file, {
    contentType: file.type || 'application/octet-stream',
    contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name || 'dokument')}`,
    customMetadata: { originalName: file.name || 'dokument' },
  });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  return authorizedPost('addReconciliationDocumentHttp', {
    businessId,
    periodId,
    fileName: file.name || 'Dokument',
    mimeType: file.type || '',
    size: file.size || 0,
    storagePath,
    downloadUrl,
    kind,
    areaKey,
    accountIds,
    requestId,
  });
};
// Backend fjerner koblingen og sier hvor filen laa. Selve filen slettes her,
// best effort: blir den liggende, er den i det minste ikke lenger dokumentasjon
// for noen konto.
const removeReconciliationDocument = async ({ businessId = runtime.activeBusinessId, periodId, documentId } = {}) => {
  const result = await authorizedPost('removeReconciliationDocumentHttp', { businessId, periodId, documentId });
  if (result?.storagePath && runtime.storage) {
    await runtime.storage.ref().child(result.storagePath).delete().catch(() => {});
  }
  return result;
};

// Kontrollene som gjelder EN periode, lest fra regnskapet selv. Avstemmingsmappa
// bruker den for aa si om arbeidet er sendt videre, og hvor det staar. Selve
// godkjenningen skjer i kontrollflaten — fire-oyne-regelen ligger i backend, og
// en knapp til her ville bare vaert en ny vei til det samme endepunktet.
const listBusinessControls = async ({ businessId = runtime.activeBusinessId, periodId } = {}) => {
  if (!runtime.db || !businessId) return [];
  let query = runtime.db.collection(`businesses/${businessId}/controls`);
  if (periodId) query = query.where('periodId', '==', periodId);
  const snapshot = await query.get();
  return snapshot.docs
    .map((entry) => ({ controlId: entry.id, ...(entry.data() || {}) }))
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
};

// Aa be om dokumentasjon er ikke det samme som aa returnere. Returen sender
// arbeidet tilbake til utforeren og avslutter kontrollorens runde; foresporselen
// holder kontrollen aapen mens det som mangler skaffes — og den blokkerer
// godkjenning til den er besvart.
// Valutakurs fra Norges Bank paa en gitt dato. Avstemmingen bruker den til aa
// regne om banksaldoen paa en valutakonto; den skriver ingenting.
const getCurrencyRate = async (payload = {}) => authorizedPost('getCurrencyRateHttp', payload);

const requestControlDocumentation = async (payload = {}) =>
  authorizedPost('requestControlDocumentationHttp', payload);
const resolveControlDocumentation = async (payload = {}) =>
  authorizedPost('resolveControlDocumentationHttp', payload);
// Hvem som skal gaa god for arbeidet. Tildelingen er en del av kontrollen og
// ikke en administrativ merkelapp: backend nekter aa sette utforeren paa eget
// arbeid, og krever at byraaet har erklaert begge som kontrollorer.
const assignControl = async (payload = {}) => authorizedPost('assignControlHttp', payload);

const getReconciliationDocumentUrl = async (storagePath = '') => {
  await initFirebase();
  const path = String(storagePath || '').trim();
  if (!path || !runtime.storage) return '';
  return runtime.storage.ref().child(path).getDownloadURL().catch(() => '');
};

// Vesentlighetsgrensen er byraaets egen faglige vurdering. Flaten sender tallet
// og begrunnelsen; serveren avviser et tall uten begrunnelse, og grensen slaar
// ingen kontroll av.
const setReconciliationMateriality = async (payload = {}) =>
  authorizedPost('setReconciliationMaterialityHttp', { businessId: runtime.activeBusinessId, ...payload });

// Periodekontrollens egne ord. Ingen av dem setter en status — de er
// dokumentasjonen ved siden av tallene, og serveren avviser en forklaring eller
// en konklusjon uten tekst.
const setReconciliationAnalytics = async (payload = {}) =>
  authorizedPost('setReconciliationAnalyticsHttp', { businessId: runtime.activeBusinessId, ...payload });
const confirmReconciliationCrossCheck = async (payload = {}) =>
  authorizedPost('confirmReconciliationCrossCheckHttp', { businessId: runtime.activeBusinessId, ...payload });
const setReconciliationConclusion = async (payload = {}) =>
  authorizedPost('setReconciliationConclusionHttp', { businessId: runtime.activeBusinessId, ...payload });

const addReconciliationRequest = async (payload = {}) => {
  return authorizedPost('addReconciliationRequestHttp', { businessId: runtime.activeBusinessId, ...payload });
};
const updateReconciliationRequest = async (payload = {}) => {
  return authorizedPost('updateReconciliationRequestHttp', { businessId: runtime.activeBusinessId, ...payload });
};

// Lesing gaar direkte mot Firestore: reglene apner samlingen for den som kan
// lese regnskapet, og flaten viser mange rader samtidig.
const listReconciliation = async ({ businessId = runtime.activeBusinessId, periodId } = {}) => {
  if (!runtime.db || !businessId || !periodId) return { period: null, accounts: [], activity: [], comments: [], documents: [], requests: [] };
  const base = runtime.db.collection('businesses').doc(businessId).collection('reconciliations').doc(periodId);
  const [periodSnapshot, accountsSnapshot, activitySnapshot, commentsSnapshot, documentsSnapshot, requestsSnapshot] = await Promise.all([
    base.get(),
    base.collection('accounts').get(),
    base.collection('activity').get(),
    base.collection('comments').get(),
    base.collection('documents').get(),
    base.collection('requests').get(),
  ]);
  const byNewestFirst = (a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
  return {
    period: periodSnapshot.exists ? { periodId, ...(periodSnapshot.data() || {}) } : null,
    accounts: accountsSnapshot.docs.map((entry) => ({ accountId: entry.id, ...(entry.data() || {}) })),
    activity: activitySnapshot.docs.map((entry) => ({ id: entry.id, ...(entry.data() || {}) })).sort(byNewestFirst),
    comments: commentsSnapshot.docs.map((entry) => ({ id: entry.id, ...(entry.data() || {}) })).sort(byNewestFirst),
    documents: documentsSnapshot.docs.map((entry) => ({ documentId: entry.id, ...(entry.data() || {}) })).sort(byNewestFirst),
    requests: requestsSnapshot.docs.map((entry) => ({ requestId: entry.id, ...(entry.data() || {}) })).sort(byNewestFirst),
  };
};
// Avstemmingsmatrisen leser byraaets speil, ikke ett periodedokument per
// klient. Se `mirrorPeriodToFirms` i functions/reconciliation.
const listFirmReconciliationStatus = async ({ firmId, periodId } = {}) => {
  if (!runtime.db || !firmId) return [];
  let query = runtime.db.collection('accountingFirms').doc(firmId).collection('reconciliationStatus');
  if (periodId) query = query.where('periodId', '==', periodId);
  const snapshot = await query.get();
  return snapshot.docs.map((entry) => ({ id: entry.id, ...(entry.data() || {}) }));
};
